import { useDeferredValue } from 'react';
import { Outlet, Route, Routes, useLocation, useParams, useSearchParams } from 'react-router-dom';

import { SuspenseBoundary } from '@components/SuspenseBoundary';
import { ErrorPage } from '@page/errorPage';
import FundsService from '../container/FundsService';
import LanguageWrapper from '../container/LanguageWrapper';
import SiteQueryContainer from '../container/SiteQueryContainer';
import { EnvironmentProviderWrapper } from '../relay/EnvironmentProviderWrapper';
import type { VisitorInfoType } from '../types/visitorInfo';
import { toggleClass } from '../utils/ponyFills';

let loader: Element | null = null;

// Defer loader query until DOM is ready
if (typeof document !== 'undefined') {
  loader = document.querySelector('.LoadingPage-overlay');

  if (!loader) {
    console.warn('[Routing] Loading overlay not found yet, will retry on DOMContentLoaded');
    document.addEventListener('DOMContentLoaded', () => {
      loader = document.querySelector('.LoadingPage-overlay');
      if (!loader) {
        console.error('[Routing] ERROR: Loading overlay element not found even after DOMContentLoaded!');
      } else {
        setupLoaderTransition();
      }
    });
  } else {
    setupLoaderTransition();
  }
}

function setupLoaderTransition() {
  if (loader) {
    loader.addEventListener('transitionend', () => {
      toggleClass(loader, 'off', loader && loader.classList.contains('fade-out'));
    });
  }
}

/**
 * Helper to build a v3-compatible location object from v6 hooks.
 * Downstream components still expect `location.query`.
 */
function useCompatRouteProps() {
  const location = useLocation();
  const params = useParams();
  const [searchParams] = useSearchParams();

  // Build a query object from search params (v3 compat)
  const query: Record<string, string> = {};
  searchParams.forEach((value, key) => {
    query[key] = value;
  });

  const compatLocation = {
    ...location,
    query,
    state: location.state || {},
  };

  return { location: compatLocation, params };
}

// Route renderer component that wraps SiteQueryContainer with environment provider and suspense
interface RouteRendererProps {
  lang: string;
  visitorInfoType: VisitorInfoType;
  needsNavigation: boolean;
  isIndex?: boolean;
}

function RouteRenderer({ lang, visitorInfoType, needsNavigation, isIndex = false }: RouteRendererProps) {
  const { location, params } = useCompatRouteProps();

  // Defer the pathname so React keeps rendering the previous page content
  // while the new Relay query loads, preventing the flash/flicker on navigation.
  const deferredPathname = useDeferredValue(location.pathname);

  return (
    <EnvironmentProviderWrapper pathname={location.pathname}>
      <SuspenseBoundary>
        <SiteQueryContainer routeProps={{ location: { ...location, pathname: deferredPathname }, params }} lang={lang} visitorInfoType={visitorInfoType} needsNavigation={needsNavigation} isIndex={isIndex} />
      </SuspenseBoundary>
    </EnvironmentProviderWrapper>
  );
}

/**
 * FundsService layout wrapper for the root route.
 * Renders FundsService with <Outlet /> for nested route content.
 */
function FundsServiceLayout() {
  return (
    <FundsService>
      <Outlet />
    </FundsService>
  );
}

/**
 * Generates the nested route elements for a given language.
 */
function languageRoutes(lang: string) {
  return (
    <>
      {/* Index route for language root */}
      <Route index={true} element={<RouteRenderer lang={lang} visitorInfoType="none" needsNavigation={true} isIndex={true} />} />

      {/* Infoservice route */}
      <Route path="infoservice" element={<RouteRenderer lang={lang} visitorInfoType="none" needsNavigation={true} />} />

      {/* GD route (guest/demo) - v3 gd(/:path) split into two routes */}
      <Route path="gd" element={<RouteRenderer lang={lang} visitorInfoType="none" needsNavigation={false} />} />
      <Route path="gd/:path" element={<RouteRenderer lang={lang} visitorInfoType="none" needsNavigation={false} />} />

      {/* Fund service routes */}
      <Route path="gfs" element={<RouteRenderer lang={lang} visitorInfoType="funds" needsNavigation={true} />} />
      <Route path="gfs/:path" element={<RouteRenderer lang={lang} visitorInfoType="funds" needsNavigation={true} />} />
      <Route path="fonds" element={<RouteRenderer lang={lang} visitorInfoType="funds" needsNavigation={true} />} />
      <Route path="fonds/:path" element={<RouteRenderer lang={lang} visitorInfoType="funds" needsNavigation={true} />} />
      <Route path="funds" element={<RouteRenderer lang={lang} visitorInfoType="funds" needsNavigation={true} />} />
      <Route path="funds/:path" element={<RouteRenderer lang={lang} visitorInfoType="funds" needsNavigation={true} />} />

      {/* Fund detail routes with sf/ prefix */}
      <Route path="sf/:fundref/:path/archive" element={<RouteRenderer lang={lang} visitorInfoType="funds" needsNavigation={true} />} />
      <Route path="sf/:fundref/:path/:path2/archive" element={<RouteRenderer lang={lang} visitorInfoType="funds" needsNavigation={true} />} />
      <Route path="sf/:fundref/:path/:path2" element={<RouteRenderer lang={lang} visitorInfoType="funds" needsNavigation={true} />} />
      <Route path="sf/:fundref/:path" element={<RouteRenderer lang={lang} visitorInfoType="funds" needsNavigation={true} />} />

      {/* Fund detail routes with gf/ prefix */}
      <Route path="gf/:path/archive" element={<RouteRenderer lang={lang} visitorInfoType="funds" needsNavigation={true} />} />
      <Route path="gf/:path/:path2/archive" element={<RouteRenderer lang={lang} visitorInfoType="funds" needsNavigation={true} />} />
      <Route path="gf/:path/:path2" element={<RouteRenderer lang={lang} visitorInfoType="funds" needsNavigation={true} />} />
      <Route path="gf/:path" element={<RouteRenderer lang={lang} visitorInfoType="funds" needsNavigation={true} />} />

      {/*
        Bare ISIN fund URLs (v3 `AT**(/archive)`, e.g. /AT0000A1TVM9 and
        /AT0000A1TVM9/archive) are intentionally NOT given a dedicated route.
        React Router v6 rewrites a trailing `*` to `/*` (a splat must follow a
        slash), so `AT*` only matches `/AT/...` and never a bare ISIN — the URL
        falls through to the generic `:path` catch-all below. Rather than fight
        the router, the ISIN is detected from the pathname in
        getEffectiveVisitorInfoType (see SiteQueryContainer), which upgrades the
        catch-all's "none" to "funds" so the disclaimer gate still runs.
      */}

      {/* Publications routes - require visitor info disclaimer */}
      <Route path="veroeffentlichungen" element={<RouteRenderer lang={lang} visitorInfoType="publications" needsNavigation={true} />} />
      <Route path="publications" element={<RouteRenderer lang={lang} visitorInfoType="publications" needsNavigation={true} />} />

      {/* Generic path routes - must come after specific routes */}
      <Route path=":path/:path2" element={<RouteRenderer lang={lang} visitorInfoType="none" needsNavigation={true} />} />
      <Route path=":path" element={<RouteRenderer lang={lang} visitorInfoType="none" needsNavigation={true} />} />

      {/* 404 for this language */}
      <Route path="*" element={<ErrorPage error={{ statusCode: 404 }} />} />
    </>
  );
}

function AppRoutes({ toggleEnvironment }: { toggleEnvironment: () => void }) {
  return (
    <Routes>
      <Route path="/" element={<FundsServiceLayout />}>
        {/* Index route for root path */}
        <Route index={true} element={<RouteRenderer lang="de" visitorInfoType="none" needsNavigation={true} isIndex={true} />} />

        {/* Language routes - LanguageWrapper as layout element with nested routes */}
        <Route path="de" element={<LanguageWrapper toggleEnvironment={toggleEnvironment} />}>
          {languageRoutes('de')}
        </Route>
        <Route path="en" element={<LanguageWrapper toggleEnvironment={toggleEnvironment} />}>
          {languageRoutes('en')}
        </Route>
        <Route path="hu" element={<LanguageWrapper toggleEnvironment={toggleEnvironment} />}>
          {languageRoutes('hu')}
        </Route>
        <Route path="cs" element={<LanguageWrapper toggleEnvironment={toggleEnvironment} />}>
          {languageRoutes('cs')}
        </Route>
        <Route path="ru" element={<LanguageWrapper toggleEnvironment={toggleEnvironment} />}>
          {languageRoutes('ru')}
        </Route>

        {/*
                  Root-level routes without language prefix.
                  The original v3 config had LanguageGroup('/', toggleEnvironment) which
                  registered all sub-routes (infoservice, gd, gfs, fonds, :path, etc.)
                  directly under '/'. This ensures paths like /infoservice, /gfs, /fonds,
                  /some-page still resolve without a language prefix.
                */}
        {languageRoutes('/')}

        {/* Global 404 */}
        <Route path="*" element={<ErrorPage error={{ statusCode: 404 }} />} />
      </Route>
    </Routes>
  );
}

export default AppRoutes;
